/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Where a part is used: the
 * panels and kits whose builds need it, at how many per build, and the demand
 * those builds create. The details page draws it (flow diagram and table) and
 * the chat explanation says it in a sentence, so both read the same numbers.
 * The builds are the panel and kit forecast the parts plan orders from.
 */
import { displayUnit } from '@/demo/procurement/data/format';
import {
  PANEL_SHOP_SITE,
  isFinishedGoodSku,
  seriesIdOf,
} from './forecast-data';
import type { FinishedGoodSku } from './forecast-data';
import {
  DEPENDENT_MONTHS,
  dependentDemand,
  isBomComponent,
} from './forecast-engine';
import type { DependentDemand } from './forecast-engine';
import { fmtCellQty, fmtQty, fmtQtyIn, fmtUnits } from './forecast-format';
import { sum } from './forecast-math';
import type { ForecastView } from './forecast-types';

/** How each panel or kit reads in a sentence. */
export const FG_NOUN: Record<FinishedGoodSku, string> = {
  'PPC-MCC-480': 'motor control panel',
  'PPC-PDP-208': 'power distribution panel',
  'KIT-VFD-50': 'drive retrofit kit',
};

/** What one build is called: a panel or a kit. */
const FG_BUILD: Record<FinishedGoodSku, string> = {
  'PPC-MCC-480': 'panel',
  'PPC-PDP-208': 'panel',
  'KIT-VFD-50': 'kit',
};

/** Builds per month from the forecast of each panel and kit, with buyer changes. */
export function adjustedPlanFrom(
  views: readonly ForecastView[],
): (finishedGood: string) => number[] {
  return (finishedGood) =>
    views.find((view) => view.id === seriesIdOf(finishedGood, PANEL_SHOP_SITE))
      ?.adjusted ?? [];
}

/** The model forecast of each panel and kit, before any buyer change. */
export function modelPlanFrom(
  views: readonly ForecastView[],
): (finishedGood: string) => number[] {
  return (finishedGood) =>
    views.find((view) => view.id === seriesIdOf(finishedGood, PANEL_SHOP_SITE))
      ?.result.forecast ?? [];
}

export function nounOf(finishedGood: string): string {
  return isFinishedGoodSku(finishedGood) ? FG_NOUN[finishedGood] : finishedGood;
}

function buildOf(finishedGood: string): string {
  return isFinishedGoodSku(finishedGood) ? FG_BUILD[finishedGood] : 'build';
}

/** "6 per panel", "400 ft per panel", "1 per kit". */
export function perBuildText(
  finishedGood: string,
  qtyPer: number,
  unit: string,
): string {
  const ft = displayUnit(unit) === 'ft' ? ' ft' : '';
  return `${fmtQtyIn(qtyPer, unit)}${ft} per ${buildOf(finishedGood)}`;
}

/** "144 panels", "1 kit". */
function buildsText(finishedGood: string, builds: number): string {
  const build = buildOf(finishedGood);
  return `${fmtQty(builds)} ${build}${builds === 1 ? '' : 's'}`;
}

export interface PartUseLine {
  finishedGood: string;
  /** "motor control panel". */
  noun: string;
  qtyPer: number;
  perBuild: string;
  /** Builds expected over the next six months. */
  builds: number;
  /** What those builds use of this part. */
  needed: number;
  /** "144 panels × 6 = 864": builds, quantity per build, and what they use. */
  detail: string;
}

export interface PartUse {
  lines: PartUseLine[];
  dependent: DependentDemand;
  /** What the builds need over the next six months, all panels and kits. */
  neededTotal: number;
  /** This part's own forecast over the same six months, adjusted. */
  ownTotal: number;
  /** The share of this location's forecast the builds account for, 0 to 100. */
  sharePercent: number;
}

/** Null when no panel or kit uses this part. */
export function partUseFor(
  view: ForecastView,
  views: readonly ForecastView[],
): PartUse | null {
  if (!isBomComponent(view.spec.sku)) return null;
  const dependent = dependentDemand(view.spec.sku, adjustedPlanFrom(views));
  const { unit } = view.spec;
  const ownTotal = sum(view.adjusted.slice(0, DEPENDENT_MONTHS));
  return {
    lines: dependent.lines.map((line) => {
      const builds = sum(line.plan);
      const needed = sum(line.implied);
      return {
        finishedGood: line.finishedGood,
        noun: nounOf(line.finishedGood),
        qtyPer: line.qtyPer,
        perBuild: perBuildText(line.finishedGood, line.qtyPer, unit),
        builds,
        needed,
        detail: `${buildsText(line.finishedGood, builds)} × ${fmtCellQty(line.qtyPer, unit)} = ${fmtCellQty(needed, unit)}`,
      };
    }),
    dependent,
    neededTotal: dependent.impliedTotal,
    ownTotal,
    sharePercent:
      ownTotal === 0 ? 0 : (dependent.impliedTotal / ownTotal) * 100,
  };
}

function joinAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "the motor control panel (6 per panel) and the drive retrofit kit (1 per kit)". */
export function usedInSentence(use: PartUse): string {
  return joinAnd(
    use.lines.map((line) => `the ${line.noun} (${line.perBuild})`),
  );
}

/** The need in a sentence, for the chat explanation. */
export function neededSentence(use: PartUse, unit: string): string {
  return `The builds expected over the next six months need ${fmtUnits(use.neededTotal, unit)}, from each panel and kit's own forecast, so a change to that forecast changes this part.`;
}
